<script setup lang="ts">
import { Filters } from "~/core/models/filters";
import { useBusinessUnit } from "~/modules/businessUnit/composables/useBusinessUnit";
import {
  PATH_BUSINESS_UNITS_ID,
  PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
} from "~/modules/businessUnit/constants";
import type { BusinessUnit } from "~/modules/businessUnit/models/BusinessUnit.interface";

const { t } = useI18n();
const router = useRouter();

const {
  isLoading,
  results,
  error,
  fetchData,
  filters,
  setFilter,
  search,
  onSearch,
} = useBusinessUnit();

const businessUnitList: Ref<BusinessUnit[]> = ref([]);

const searchQuery = computed({
  get() {
    return (filters.get(Filters.BUSINESS_UNIT_NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.BUSINESS_UNIT_NAME, searchQuery);
  },
});

async function loadData() {
  await fetchData();
  if (!error.value) {
    businessUnitList.value = results.value as BusinessUnit[];
  }
}

function onSettingsClick(businessUnitCode: number) {
  router.push({
    name: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
    params: { id: businessUnitCode },
  });
}

function onItemClick(businessUnit: BusinessUnit) {
  router.push({
    name: PATH_BUSINESS_UNITS_ID,
    params: { id: businessUnit.code },
  });
}

onMounted(() => onSearch(loadData));
</script>

<template>
  <div class="mb-6 w-1/2">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('filters.search')"
      @clear="search"
      @search="search"
    />
  </div>
  <container-component :is-loading="isLoading" :error="error" :px="0" :py="0">
    <card-list :item-list="businessUnitList" @click:item="onItemClick">
      <!-- Define slot with type-safe props -->
      <template #cardContent="{ item: businessUnit }: { item: BusinessUnit }">
        <entity-card-content
          :entity-name="businessUnit.name"
          :device-count="businessUnit.deviceCount"
          @click:settings="onSettingsClick(businessUnit.code)"
        />
      </template>
    </card-list>
  </container-component>
</template>
